import { controlAnswerKey } from "../../utils/regulatoryIdentity"
import {
  PrintControlCard,
  useMeasuredControlPages,
} from "../../components/en1176/RegulatoryPrintCards"
import { answerStatus, lightReportControls } from "../../utils/regulatoryAnswers"
import {
  FilePdfOutlined,
  FilterOutlined,
  GlobalOutlined,
  LeftCircleOutlined,
  SignatureOutlined,
} from "@ant-design/icons"
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Col,
  Divider,
  FloatButton,
  Popover,
  Row,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd"
import { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  useRegulatoryChildren,
  useRegulatoryReportAnswers,
} from "../../components/en1176/useRegulatoryAnswers"
import { controlGroup, legacyControls, regulatoryUuid } from "../../utils/regulatoryPresentation"
import { useAssignedCatalogs } from "../Regulatory/useOperationalCatalog"

import { EResult } from "../../calculus/types"
import { FlexCol, FlexRow } from "../../components/commons-ts/common"
import { GoogleAddressWidget } from "../../components/commons-ts/googleMaps"
import { TableImageThumbnail } from "../../components/commons-ts/tableImageThumbnail"
import { TranslationLanguageSelectWidget } from "../../components/commons-ts/translationLanguageSelectWidget"
import { controlPointIDRender } from "../../components/en1176/EN1176QuestionnaireWidget"
import { FOOTER_HEIGHT, HEADER_HEIGHT } from "../../components/report-ts"
import { PDFEmptySpacePlaceholder } from "../../components/report-ts/reportEmptySpacePlaceholder"
import { ImageUploader } from "../../components/report-ts/reportImageUploader"
import {
  PDFControlledInput,
  PDFInput,
  PDFInputTextArea,
} from "../../components/report-ts/reportInputs"
import { FirstPicture } from "../../components/report-ts/reportPicturesRow"
import {
  Annex,
  Equipment,
  RegulatoryControlAnswerStatus,
  Site,
  UUID,
} from "../../generated/proto-ts/main"
import { ReportConfigProvider, useReportConfigContext } from "../../providers/reportConfigProvider"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { useTranslationContext } from "../../providers/translationProvider"
import {
  ICloudCustomer,
  ICloudDevice,
  ICloudRegulatoryControlPoint,
  ICloudRegulatoryControlTemplate,
  ICloudUser,
  IEN1176AnswerMap,
  calculateEN1176Stats,
} from "../../types"
import {
  useCloudUsercommSiteAnnexesWS,
  useCloudUsercommSiteWS,
} from "../../usercomm/cloud/cloudUsercommAsyncRequestWS"
import { callCloudApiV2 } from "../../utils/cloudApiV2"
import { Translated } from "../../utils/translated"
import {
  COLOR_BG_GRAY,
  formatDayOrLongDash,
  parsePathForEntityUUID,
  pbUUIDToUuid,
  uuidToPbUUID,
} from "../../utils/utils"

const PDF_INPUT_TEXT_AREA_STYLE = {
  fontSize: 14,
}

const PDF_PAGE_CONTENT_MARGIN_TOP = 10

const PDF_EN1176_MAX_IDENTIFICATION_EQUIPMENTS_PER_PAGE = 5
const PDF_EN1176_MAX_IDENTIFICATION_EQUIPMENTS_WITH_SUPPLEMENTARY_SECTIONS = 2

type IEN1176ReportReference = NonNullable<ICloudRegulatoryControlPoint["Reference"]> & {
  Code: string
}

type IEN1176ControlPointPhotoAnnex = {
  key: string
  context: string
  controlPoint: ICloudRegulatoryControlPoint
  pictures: UUID[]
  comment: string
}

enum PDFEquipmentPageMode {
  SinglePage = 0,
  MultiPage = 1,
}

type EN1176ReportMode = "full" | "light"

const getStatusSummary = answerStatus

const getLightControlPoints = lightReportControls

const getEN1176GlobalResult = (
  siteControlPoints: ICloudRegulatoryControlPoint[],
  siteAnswers: IEN1176AnswerMap,
  equipments: Equipment[],
  equipmentControlPointsMap: Record<string, ICloudRegulatoryControlPoint[]>,
  equipmentAnswersMap: Record<string, IEN1176AnswerMap>,
): EResult => {
  let hasNonConform = false
  let hasToBeChecked = false
  let hasUnanswered = siteControlPoints.length === 0 && equipments.length === 0

  const checkAnswers = (
    controlPoints: ICloudRegulatoryControlPoint[],
    answers: IEN1176AnswerMap,
  ) => {
    for (const cp of controlPoints) {
      const ans = answers[controlAnswerKey(cp)]
      if (ans && !regulatoryUuid(ans.control_point_revision_uuid)) hasUnanswered = true
      if (!ans || ans.status === RegulatoryControlAnswerStatus.UNKNOWN_STATUS) {
        hasUnanswered = true
      } else if (ans.status === RegulatoryControlAnswerStatus.NON_CONFORM) {
        hasNonConform = true
      } else if (
        [
          RegulatoryControlAnswerStatus.TO_BE_CHECKED,
          RegulatoryControlAnswerStatus.OBSERVATION,
          RegulatoryControlAnswerStatus.NOT_VERIFIED,
        ].includes(ans.status)
      ) {
        hasToBeChecked = true
      }
    }
  }

  checkAnswers(siteControlPoints, siteAnswers)
  for (const equipment of equipments) {
    const equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
    const controlPoints = equipmentControlPointsMap[equipmentUUIDStr] ?? []
    const answers = equipmentAnswersMap[equipmentUUIDStr] ?? {}
    if (!controlPoints.length) hasUnanswered = true
    checkAnswers(controlPoints, answers)
  }

  if (hasNonConform) {
    return EResult.NotAdequate
  }
  if (hasToBeChecked || hasUnanswered) {
    return EResult.ToReview
  }
  return EResult.Adequate
}

const formatEN1176GlobalResultSummary = (
  siteControlPoints: ICloudRegulatoryControlPoint[],
  siteAnswers: IEN1176AnswerMap,
  equipments: Equipment[],
  equipmentControlPointsMap: Record<string, ICloudRegulatoryControlPoint[]>,
  equipmentAnswersMap: Record<string, IEN1176AnswerMap>,
): string => {
  const equipmentResults = {
    conformes: 0,
    nonConformes: 0,
    aSurveiller: 0,
  }

  for (const equipment of equipments) {
    const result = getEN1176GlobalResult(
      [],
      {},
      [equipment],
      equipmentControlPointsMap,
      equipmentAnswersMap,
    )
    if (result === EResult.Adequate) equipmentResults.conformes++
    else if (result === EResult.NotAdequate) equipmentResults.nonConformes++
    else equipmentResults.aSurveiller++
  }

  const total = equipments.length
  let equipmentText = "aucun équipement."
  if (total === 1) {
    if (equipmentResults.conformes === total) {
      equipmentText = "1 équipement, sans anomalie déclarée dans les réponses renseignées."
    } else if (equipmentResults.nonConformes === total) {
      equipmentText = "1 équipement, qui n'est pas conforme."
    } else if (equipmentResults.aSurveiller === total) {
      equipmentText = "1 équipement, qui nécessite une attention."
    }
  } else if (total > 1 && equipmentResults.conformes === total) {
    equipmentText = `${total} équipements, sans anomalie déclarée dans les réponses renseignées.`
  } else if (total > 0) {
    const resultParts = [
      equipmentResults.conformes > 0 &&
        `${equipmentResults.conformes} ${equipmentResults.conformes === 1 ? "équipement conforme" : "équipements conformes"}`,
      equipmentResults.nonConformes > 0 &&
        `${equipmentResults.nonConformes} ${equipmentResults.nonConformes === 1 ? "équipement non conforme" : "équipements non conformes"}`,
      equipmentResults.aSurveiller > 0 &&
        `${equipmentResults.aSurveiller} ${equipmentResults.aSurveiller === 1 ? "élément nécessitant une surveillance" : "éléments nécessitant une surveillance"}`,
    ].filter(Boolean)
    equipmentText = `${total} équipements : ${resultParts.join(", ")}.`
  }

  const siteResult = getEN1176GlobalResult(
    siteControlPoints,
    siteAnswers,
    [],
    equipmentControlPointsMap,
    equipmentAnswersMap,
  )
  const siteResultText =
    siteResult === EResult.Adequate
      ? "ne présente pas d’anomalie déclarée dans les réponses renseignées"
      : siteResult === EResult.NotAdequate
        ? "n'est pas conforme"
        : "nécessite une attention"
  return `Le présent site, qui ${siteResultText}, comporte ${equipmentText}`
}

const PDFHeader: FC<{
  site: Site | null
  user: ICloudUser | null
}> = ({ site, user }) => {
  const {
    leftDataURL,
    rightHeaderReferenceNote,
    rightHeaderDateNote,
    rightHeaderVersionNote,
    setLeftDataURL,
    setRightHeaderReferenceNote,
    setRightHeaderDateNote,
    setRightHeaderVersionNote,
  } = useReportConfigContext()

  useEffect(() => {
    if (!site) {
      return
    }
    const ref = `${site.mission_name} ${site.client_name} ${site.site_name}`
    setRightHeaderReferenceNote(ref)
  }, [site, setRightHeaderReferenceNote])

  useEffect(() => {
    if (site === null) {
      return
    }
    const normTypeStr = "EN 1176"
    const documentName = `${rightHeaderDateNote}_${normTypeStr}_${rightHeaderReferenceNote}_v${rightHeaderVersionNote}`
    document.title = window.parent.document.title = documentName
  }, [site, rightHeaderDateNote, rightHeaderReferenceNote, rightHeaderVersionNote])

  useEffect(() => {
    setRightHeaderDateNote(dayjs().format("YYYY-MM-DD"))
    setRightHeaderVersionNote("1.0")
  }, [setRightHeaderDateNote, setRightHeaderVersionNote])

  const memoEnterpriseLogoDataUrl = useMemo(() => {
    if (!user) {
      return null
    }
    if (user.Enterprise === null || user.Enterprise.EnterprisePictureUUID === null) {
      return null
    }
    return `/api/uploads/${user.Enterprise.EnterprisePictureUUID}`
  }, [user])

  return (
    <header
      style={{
        borderBottom: `3px solid #ccc`,
        fontSize: "0.9rem",
        textAlign: "center",
      }}
    >
      <FlexRow
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          height: HEADER_HEIGHT,
          marginBottom: 5,
        }}
      >
        <ImageUploader
          dataURL={memoEnterpriseLogoDataUrl ?? leftDataURL}
          setDataURL={setLeftDataURL}
        />
        <FlexCol
          style={{
            alignItems: "end",
            gap: 0,
            width: "100%",
          }}
        >
          <span>
            <b>
              <PDFControlledInput
                value={rightHeaderReferenceNote ?? ""}
                setValue={(v) => setRightHeaderReferenceNote(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "end" },
                }}
              />
            </b>
          </span>
          <span>
            <Translated keyEn="Edited on" />:{" "}
            <b>
              <PDFControlledInput
                value={rightHeaderDateNote ?? ""}
                setValue={(v) => setRightHeaderDateNote(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "end" },
                }}
              />
            </b>
          </span>
          <span>
            <Translated keyEn="Version" />:{" "}
            <b>
              <PDFControlledInput
                value={rightHeaderVersionNote ?? ""}
                setValue={(v) => setRightHeaderVersionNote(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "end" },
                }}
              />
            </b>
          </span>
        </FlexCol>
      </FlexRow>
    </header>
  )
}

const PDFFootnoteTextArea: FC<{
  footnote: string | null
  setFootnote: (footnote: string | null) => void
  textAlign: "center" | "left" | "right"
}> = ({ footnote, setFootnote, textAlign }) => {
  return (
    <span
      style={{
        textAlign: "center",
        width: "100%",
      }}
    >
      <PDFInputTextArea
        style={{
          textAlign,
        }}
        autoSize={{
          minRows: 2,
          maxRows: 2,
        }}
        value={footnote ?? ""}
        onChange={(e) => {
          setFootnote(e.target.value)
        }}
      />
    </span>
  )
}

const PDFFooter: FC<{
  pageNumber: number
  totalPages: number
}> = ({ pageNumber, totalPages }) => {
  const { translated } = useTranslationContext()
  const { leftFootnote, rightFootnote, setLeftFootnote, setRightFootnote } =
    useReportConfigContext()

  useEffect(() => {
    if (rightFootnote === "") {
      setRightFootnote(
        translated(
          "Reproduction of this inspection report is only permitted in the form of a complete photographic facsimile",
        ),
      )
    }
  }, [rightFootnote, setRightFootnote, translated])

  return (
    <footer
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        marginLeft: "8mm",
        marginRight: "8mm",
        width: "calc(100% - 16mm)",
        height: FOOTER_HEIGHT,
        borderTop: `3px solid #ccc`,
        paddingTop: 5,
      }}
    >
      <FlexRow
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.7rem",
        }}
      >
        <PDFFootnoteTextArea
          footnote={leftFootnote}
          setFootnote={setLeftFootnote}
          textAlign="left"
        />
        <p style={{ width: "fit-content", whiteSpace: "nowrap" }}>
          {translated("Page")} {pageNumber} / {totalPages}
        </p>
        <PDFFootnoteTextArea
          footnote={rightFootnote}
          setFootnote={setRightFootnote}
          textAlign="right"
        />
      </FlexRow>
    </footer>
  )
}

const PDFPage: FC<{
  site: Site | null
  user: ICloudUser | null
  pageNumber: number
  totalPages: number
  children: React.ReactNode
}> = ({ site, user, pageNumber, totalPages, children }) => {
  return (
    <section
      className="sheet p-8mm"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
      }}
    >
      <PDFHeader site={site} user={user} />
      {children}
      <PDFFooter pageNumber={pageNumber} totalPages={totalPages} />
    </section>
  )
}

const ReportNumberedTitle: FC<{ title: ReactElement; number: number }> = ({ title, number }) => {
  return (
    <Typography.Text style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
      {number}. {title}
    </Typography.Text>
  )
}

const EN1176QuestionnaireTable_Print: FC<{
  controlPoints: ICloudRegulatoryControlPoint[]
  answers: IEN1176AnswerMap
  referenceCodes: Record<string, string>
  startIdx?: number
}> = ({ controlPoints, answers, referenceCodes, startIdx = 0 }) => {
  return (
    <div style={{ width: "100%" }}>
      {controlPoints.map((cp, i) => (
        <PrintControlCard key={`${controlAnswerKey(cp)}/${i}`} point={cp} answers={answers} />
      ))}
    </div>
  )
}

const FirstPageContent: FC<{
  user: ICloudUser | null
  site: Site
  globalResultSummary: string
  enterpriseCustomers: ICloudCustomer[] | null
}> = ({ user, site, globalResultSummary, enterpriseCustomers }) => {
  const {
    leftSignatureDataURL,
    rightSignatureDataURL,
    leftSignatureName,
    setLeftSignatureDataURL,
    setRightSignatureDataURL,
    setLeftSignatureName,
  } = useReportConfigContext()

  const [clientAddressValue, setClientAddressValue] = useState<string | null>(null)
  const [clientContactValue, setClientContactValue] = useState<string | null>(null)

  useEffect(() => {
    const customer = enterpriseCustomers?.find((customer) => customer.Name === site.client_name)
    if (customer !== undefined) {
      setClientAddressValue(customer.PrimaryAddress ?? "")
      setClientContactValue(customer.Contact ?? "")
    }
  }, [enterpriseCustomers, site.client_name])

  const memoSitePicture = useMemo(() => {
    if (!site.pictures || site.pictures.length === 0) {
      return null
    }
    return (
      <img
        className="max-h-80mm object-contain border-2 border-primary-dark"
        style={{ maxWidth: "80%" }}
        src={`/api/uploads/${pbUUIDToUuid(site.pictures[0])}`}
        alt="site"
      />
    )
  }, [site])

  const memoUserFullName = useMemo(() => {
    if (!user) {
      return <PDFInput />
    }
    return <PDFInput key={Math.random()} defaultValue={`${user.FirstName} ${user.LastName}`} />
  }, [user])

  const memoUserPhoneNumber = useMemo(() => {
    if (!user) {
      return <PDFInput />
    }
    let value = user.PhoneNumber
    if (user.Enterprise !== null && user.Enterprise.EnterprisePhoneNumber) {
      value = user.Enterprise.EnterprisePhoneNumber
    }
    return <PDFInput key={Math.random()} defaultValue={value} />
  }, [user])

  const memoUserEmail = useMemo(() => {
    if (!user) {
      return <PDFInput />
    }
    let value = user.Email
    if (user.Enterprise !== null && user.Enterprise.EnterpriseEmail) {
      value = user.Enterprise.EnterpriseEmail
    }
    return <PDFInput key={Math.random()} defaultValue={value} />
  }, [user])

  const memoUserEnterpriseCofracPictureUrl = useMemo((): string | null => {
    if (!user || !user.Enterprise || !user.Enterprise.CofracPictureUUID) {
      return null
    }
    return `/api/uploads/${user.Enterprise.CofracPictureUUID}`
  }, [user])

  const memoUserEnterpriseCofracLegalNotice = useMemo((): string | null => {
    if (!user || !user.Enterprise) {
      return null
    }
    return user.Enterprise.CofracLegalNotice
  }, [user])

  const memoSignaturesRow = useMemo(() => {
    let userPDFInput = (
      <PDFInput
        key={"nouser"}
        defaultValue={"   "}
        style={{ width: "100%", textAlign: "center" }}
      />
    )
    if (user !== null) {
      userPDFInput = (
        <PDFInput
          key={user.UUID}
          defaultValue={`${user.FirstName} ${user.LastName}`}
          style={{ width: "fit-content", textAlign: "center" }}
        />
      )
    }

    return (
      <Row justify="space-between">
        <Col xs={12}>
          <FlexCol
            style={{
              height: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ width: "100%", textAlign: "center" }}>
              <Translated keyEn="Head of inspection" />
            </span>
            <FlexCol style={{ alignItems: "center" }}>
              <ImageUploader
                dataURL={leftSignatureDataURL}
                setDataURL={setLeftSignatureDataURL}
                size={50}
                icon={
                  <SignatureOutlined
                    className="pdf-invisible"
                    style={{ fontSize: "2rem", color: COLOR_BG_GRAY }}
                  />
                }
              />
              <PDFControlledInput
                value={leftSignatureName ?? ""}
                setValue={(v) => setLeftSignatureName(v?.toString() ?? "")}
                inputProps={{
                  style: { width: "fit-content", textAlign: "center" },
                }}
              />
            </FlexCol>
          </FlexCol>
        </Col>
        <Col xs={12}>
          <FlexCol
            style={{
              height: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              <Translated keyEn="Inspector" />
            </span>
            <FlexCol style={{ alignItems: "center" }}>
              <ImageUploader
                dataURL={rightSignatureDataURL}
                setDataURL={setRightSignatureDataURL}
                size={50}
                icon={
                  <SignatureOutlined
                    className="pdf-invisible"
                    style={{ fontSize: "2rem", color: COLOR_BG_GRAY }}
                  />
                }
              />
              {userPDFInput}
            </FlexCol>
          </FlexCol>
        </Col>
      </Row>
    )
  }, [
    user,
    leftSignatureDataURL,
    rightSignatureDataURL,
    leftSignatureName,
    setLeftSignatureDataURL,
    setRightSignatureDataURL,
    setLeftSignatureName,
  ])

  const memoCofracRow = useMemo(() => {
    if (
      memoUserEnterpriseCofracPictureUrl === null ||
      memoUserEnterpriseCofracLegalNotice === null
    ) {
      return null
    }
    return (
      <FlexRow
        style={{
          justifyContent: "center",
          alignItems: "end",
          gap: 0,
        }}
      >
        <ImageUploader
          dataURL={memoUserEnterpriseCofracPictureUrl}
          setDataURL={() => null}
          size={50}
        />
        <PDFInputTextArea
          defaultValue={memoUserEnterpriseCofracLegalNotice}
          autoSize
          style={{ fontSize: "0.7rem", marginLeft: 0 }}
        />
      </FlexRow>
    )
  }, [memoUserEnterpriseCofracLegalNotice, memoUserEnterpriseCofracPictureUrl])

  const memoClientContactInput = useMemo(() => {
    let text = ""
    if (clientContactValue !== null && clientContactValue !== "") {
      text += clientContactValue
    } else {
      text += "—"
    }
    if (clientAddressValue !== null && clientAddressValue !== "") {
      text += "\n" + clientAddressValue
    }
    return (
      <PDFInputTextArea
        key={Math.random()}
        defaultValue={text}
        autoSize={{ minRows: 2 }}
        style={{ width: "fit-content", minWidth: "50%" }}
      />
    )
  }, [clientContactValue, clientAddressValue])

  return (
    <FlexCol
      style={{
        justifyContent: "space-between",
        alignItems: "start",
        height: `100%`,
        paddingBottom: FOOTER_HEIGHT,
      }}
    >
      <FlexCol
        style={{
          alignItems: "start",
          gap: 0,
          width: "100%",
        }}
      >
        <span style={{ fontSize: "1rem", fontWeight: "bold", marginTop: 25, marginLeft: 2 }}>
          <Translated keyEn="Delivered to" />:
        </span>
        <b>
          <PDFInput defaultValue={site.client_name} />
        </b>
        {memoClientContactInput}
      </FlexCol>

      <FlexCol
        style={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0,
        }}
      >
        <FlexRow
          style={{
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "1.7rem",
          }}
        >
          <span>
            <Translated keyEn="Mission" />:
          </span>
          <span>
            <PDFInput defaultValue={site.mission_name} style={{ width: "fit-content" }} />
          </span>
        </FlexRow>
        <FlexRow
          style={{
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "1.3rem",
            borderBottom: `2px solid black`,
          }}
        >
          <span>
            <Translated keyEn="Site" />:
          </span>
          <span>
            <PDFInput defaultValue={site.site_name} style={{ width: "fit-content" }} />
          </span>
        </FlexRow>
        <Divider />
        <Row gutter={[10, 10]} justify="center" style={{ width: "100%" }}>
          <Col xs={14}>
            <FlexCol style={{ gap: 0 }}>
              <FlexCol>
                <span style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                  <Translated keyEn="Site address" />:
                </span>
                <PDFInputTextArea defaultValue={site.address} autoSize />
              </FlexCol>
              <FlexRow style={{ alignItems: "center" }}>
                <span style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                  <Translated keyEn="Inspection date" />:
                </span>
                <PDFInput defaultValue={new Date(site.execution_date).toLocaleDateString()} />
              </FlexRow>
              <span style={{ fontWeight: "bold", marginTop: 25, marginBottom: 5 }}>
                <Translated keyEn="Inspected by" />:
              </span>
              <FlexRow style={{ alignItems: "center" }}>
                <span style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                  <Translated keyEn="Inspector" />:
                </span>
                {memoUserFullName}
              </FlexRow>
              <FlexRow style={{ alignItems: "center" }}>
                <span style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                  <Translated keyEn="Email" />:{" "}
                </span>
                {memoUserEmail}
              </FlexRow>
              <FlexRow style={{ alignItems: "center" }}>
                <span style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                  <Translated keyEn="Phone" />:{" "}
                </span>
                {memoUserPhoneNumber}
              </FlexRow>
            </FlexCol>
          </Col>
          <Col xs={10}>{memoSitePicture}</Col>
        </Row>
      </FlexCol>

      <FlexRow
        style={{
          justifyContent: "center",
          alignItems: "center",
          width: "60%",
        }}
      >
        <PDFInputTextArea
          defaultValue={globalResultSummary}
          autoSize={{ minRows: 2 }}
          style={{ width: "100%", textAlign: "left", fontWeight: "bold" }}
        />
      </FlexRow>

      <Row justify="space-between" align="bottom" style={{ width: "100%" }}>
        <Col xs={12}>{memoCofracRow}</Col>
        <Col xs={12}>{memoSignaturesRow}</Col>
      </Row>
    </FlexCol>
  )
}

const SecondPageContent: FC<{
  site: Site
  equipments: Equipment[]
  templates: ICloudRegulatoryControlTemplate[]
  equipmentControlPointsMap: Record<string, ICloudRegulatoryControlPoint[]>
  equipmentAnswersMap: Record<string, IEN1176AnswerMap>
  isIdentificationContinued: boolean
}> = ({
  site,
  equipments,
  templates,
  equipmentControlPointsMap,
  equipmentAnswersMap,
  isIdentificationContinued,
}) => {
  const { translated } = useTranslationContext()

  const memoMustBeTranslated = useCallback(
    (value: string) => {
      return translated(value) ?? ""
    },
    [translated],
  )

  const memoIntroductionInput = useMemo((): ReactElement => {
    let text = memoMustBeTranslated(
      "The following report presents the results of the safety inspection carried out on the site",
    )
    text += ` "${site.site_name}" `
    text +=
      memoMustBeTranslated("in accordance with the norm") +
      " EN 1176 (Playground equipment and surfacing - Safety requirements and test methods)."
    text += " " + memoMustBeTranslated("on") + " " + formatDayOrLongDash(site.execution_date) + "."
    return <PDFInputTextArea key={Math.random()} defaultValue={text} autoSize={{ minRows: 3 }} />
  }, [memoMustBeTranslated, site])

  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      {!isIdentificationContinued && (
        <>
          <ReportNumberedTitle number={1} title={<Translated keyEn="Introduction" />} />
          <div style={PDF_INPUT_TEXT_AREA_STYLE}>{memoIntroductionInput}</div>
        </>
      )}

      {!isIdentificationContinued && (
        <>
          <ReportNumberedTitle number={2} title={<Translated keyEn="Mandatory Signage" />} />
          {site.pictures[1] === undefined ? (
            <div style={PDF_INPUT_TEXT_AREA_STYLE}>
              <PDFInputTextArea defaultValue="N/A" autoSize={{ minRows: 1 }} />
            </div>
          ) : (
            <FlexCol style={{ alignItems: "center", gap: 4 }}>
              <img
                className="max-h-60mm object-contain border-2 border-primary-dark"
                style={{ maxWidth: "80%" }}
                src={`/api/uploads/${pbUUIDToUuid(site.pictures[1])}`}
                alt="mandatory signage"
              />
              <Typography.Text type="secondary" style={{ fontSize: "0.8rem", textAlign: "center" }}>
                <Translated keyEn="Photograph of mandatory signage displayed at the site." />
              </Typography.Text>
            </FlexCol>
          )}
        </>
      )}

      <ReportNumberedTitle
        number={3}
        title={
          <Translated
            keyEn={isIdentificationContinued ? "Identification (continued)" : "Identification"}
          />
        }
      />
      <EN1176EquipmentIdentificationTable
        equipments={equipments}
        templates={templates}
        equipmentControlPointsMap={equipmentControlPointsMap}
        equipmentAnswersMap={equipmentAnswersMap}
      />
      {!isIdentificationContinued && <MethodologyContent />}
    </FlexCol>
  )
}

const EN1176EquipmentIdentificationTable: FC<{
  equipments: Equipment[]
  templates: ICloudRegulatoryControlTemplate[]
  equipmentControlPointsMap: Record<string, ICloudRegulatoryControlPoint[]>
  equipmentAnswersMap: Record<string, IEN1176AnswerMap>
}> = ({ equipments, templates, equipmentControlPointsMap, equipmentAnswersMap }) => {
  const columns = useMemo(
    (): ColumnsType<Equipment> => [
      {
        title: <Translated keyEn="Equipment" />,
        render: (_, equipment) => equipment.equipment_name || "-",
      },
      {
        title: <Translated keyEn="Manufacturer" />,
        render: (_, equipment) => equipment.equipment_manufacturer || "-",
      },
      {
        title: <Translated keyEn="Reference" />,
        render: (_, equipment) => equipment.equipment_reference || "-",
      },
      {
        title: <Translated keyEn="Installation date" />,
        render: (_, equipment) => formatDayOrLongDash(equipment.equipment_installation_date, true),
      },
      {
        title: <Translated keyEn="Inspection model" />,
        render: (_, equipment) =>
          equipmentControlPointsMap[regulatoryUuid(equipment.uuid)]?.some(
            (p) => p.CompositionEnabled,
          )
            ? "Modèles par composant"
            : templates.find(
                (template) => template.UUID === regulatoryUuid(equipment.regulatory_template_uuid),
              )?.Name ||
              (equipmentControlPointsMap[regulatoryUuid(equipment.uuid)]?.some(
                (p) => p.ContextLabel,
              )
                ? "Modèles par composant"
                : "-"),
      },
      {
        title: <Translated keyEn="Result" />,
        width: "10rem",
        render: (_, equipment) => {
          const equipmentUUID = pbUUIDToUuid(equipment.uuid)
          const controlPoints = equipmentControlPointsMap[equipmentUUID] || []
          const stats = calculateEN1176Stats(
            controlPoints.map(controlAnswerKey),
            equipmentAnswersMap[equipmentUUID] || {},
          )
          const unrespondedCount = stats.total - stats.answered

          if (stats.total === 0) {
            return "-"
          }

          return (
            <FlexRow style={{ flexWrap: "wrap", gap: 2 }}>
              {stats.conformeCount > 0 && (
                <Badge
                  size="small"
                  count={stats.conformeCount}
                  style={{ backgroundColor: "#52c41a", zIndex: 1 }}
                >
                  <Tag color="success">C</Tag>
                </Badge>
              )}
              {stats.nonConformeCount > 0 && (
                <Badge
                  size="small"
                  count={stats.nonConformeCount}
                  style={{ backgroundColor: "#ff4d4f", zIndex: 1 }}
                >
                  <Tag color="error">NC</Tag>
                </Badge>
              )}
              {stats.aControlerCount > 0 && (
                <Badge
                  size="small"
                  count={stats.aControlerCount}
                  style={{ backgroundColor: "#faad14", zIndex: 1 }}
                >
                  <Tag color="warning">TBC</Tag>
                </Badge>
              )}
              {stats.observationCount > 0 && <Tag color="blue">{stats.observationCount} O</Tag>}
              {stats.notVerifiedCount > 0 && <Tag color="orange">{stats.notVerifiedCount} NV</Tag>}
              {stats.naCount > 0 && (
                <Badge size="small" count={stats.naCount} style={{ zIndex: 1 }}>
                  <Tag>NA</Tag>
                </Badge>
              )}
              {/* {unrespondedCount > 0 && (
                <Badge size="small" count={unrespondedCount}>
                  <Tag>NR</Tag>
                </Badge>
              )} */}
            </FlexRow>
          )
        },
      },
    ],
    [templates, equipmentControlPointsMap, equipmentAnswersMap],
  )
  return (
    <Table
      rootClassName="printed-table"
      dataSource={equipments}
      columns={columns}
      pagination={false}
      rowKey={(equipment) => pbUUIDToUuid(equipment.uuid)}
      size="small"
      bordered
      style={{ width: "100%" }}
    />
  )
}

const InspectionEquipmentContent: FC<{
  devices: ICloudDevice[]
  selectedDeviceUUID: string | null
  setSelectedDeviceUUID: (deviceUUID: string | null) => void
}> = ({ devices, selectedDeviceUUID, setSelectedDeviceUUID }) => {
  const { translated } = useTranslationContext()
  const selectedDevice = devices.find((device) => device.UUID === selectedDeviceUUID)

  const getDeviceLabel = (device: ICloudDevice) =>
    `${device.DeviceType?.Name ?? "Gauge equipment"} - ${device.Name}`

  return (
    <>
      <ReportNumberedTitle number={6} title={<Translated keyEn="Inspection Equipment" />} />
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>
        <PDFInputTextArea
          defaultValue={
            translated(
              "The inspection was carried out using the following calibrated gauge equipment:",
            ) ?? ""
          }
          autoSize={{ minRows: 2 }}
        />
      </div>
      <div
        className="pdf-invisible"
        style={devices.length > 5 ? { maxHeight: 180, overflowY: "auto" } : undefined}
      >
        <FlexCol style={{ gap: 6 }}>
          {devices.map((device) => {
            const isSelected = device.UUID === selectedDeviceUUID

            return (
              <div key={device.UUID}>
                <FlexRow style={{ alignItems: "center", gap: 8 }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(event) =>
                      setSelectedDeviceUUID(event.target.checked ? device.UUID : null)
                    }
                  />
                  <span>{getDeviceLabel(device)}</span>
                </FlexRow>
              </div>
            )
          })}
        </FlexCol>
      </div>
      <span
        className="pdf-only"
        style={{
          ...PDF_INPUT_TEXT_AREA_STYLE,
          marginLeft: 16,
          fontFamily: "inherit",
        }}
      >
        {selectedDevice ? getDeviceLabel(selectedDevice) : null}
      </span>
    </>
  )
}

const MethodologyContent: FC = () => {
  const { translated } = useTranslationContext()
  const methodology = useMemo(
    () =>
      translated(
        "Ce rapport présente les réponses enregistrées pour le site et ses équipements, selon les modèles et révisions indiqués. Les résultats possibles sont : Conforme, Non-conforme, À contrôler, Observation, Non vérifié ou N/A ; un point sans réponse reste non répondu. Les limites de couverture et réserves du catalogue doivent être examinées. Les définitions correspondent aux révisions affectées ; les anciennes affectations sans révision restent identifiées comme inconnues.",
      ) ?? "",
    [translated],
  )
  return (
    <>
      <ReportNumberedTitle number={4} title={<Translated keyEn="Methodology" />} />
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>
        <PDFInputTextArea defaultValue={methodology} autoSize={{ minRows: 3 }} />
      </div>
    </>
  )
}

const ThirdPageContent: FC<{
  site: Site
  references: IEN1176ReportReference[]
  devices: ICloudDevice[]
  selectedDeviceUUID: string | null
  setSelectedDeviceUUID: (deviceUUID: string | null) => void
}> = ({ site, references, devices, selectedDeviceUUID, setSelectedDeviceUUID }) => {
  const referenceColumns = useMemo(
    (): ColumnsType<IEN1176ReportReference> => [
      { title: <Translated keyEn="Code" />, dataIndex: "Code", width: "5rem" },
      { title: <Translated keyEn="Reference" />, dataIndex: "Name" },
      { title: <Translated keyEn="Title" />, dataIndex: "Title" },
    ],
    [],
  )

  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      <ReportNumberedTitle number={5} title={<Translated keyEn="References" />} />
      <Table
        rootClassName="printed-table"
        dataSource={references}
        columns={referenceColumns}
        pagination={false}
        rowKey="UUID"
        size="small"
        bordered
        style={{ width: "100%" }}
      />
      <InspectionEquipmentContent
        devices={devices}
        selectedDeviceUUID={selectedDeviceUUID}
        setSelectedDeviceUUID={setSelectedDeviceUUID}
      />
      <ReportNumberedTitle number={7} title={<Translated keyEn="Geolocation" />} />
      <GoogleAddressWidget
        address={site.address}
        setAddress={() => null}
        points={[]}
        autocompleteClassName="pdf-invisible"
      />
      {/* Comments */}
      <div style={PDF_INPUT_TEXT_AREA_STYLE}>
        <PDFInputTextArea defaultValue={""} autoSize={{ minRows: 2 }} />
      </div>
    </FlexCol>
  )
}

const SiteQuestionnairePageContent: FC<{
  site: Site
  template: ICloudRegulatoryControlTemplate | null
  controlPoints: ICloudRegulatoryControlPoint[]
  answers: IEN1176AnswerMap
  referenceCodes: Record<string, string>
  isContinued: boolean
}> = ({ site, template, controlPoints, answers, referenceCodes, isContinued }) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      <h2 style={{ width: "100%" }}>
        <FlexRow style={{ alignItems: "center", width: "100%" }}>
          <span>
            <Translated keyEn="Site control points" />
          </span>
          {isContinued && (
            <span style={{ fontSize: "1rem", fontWeight: "normal" }}>
              {" "}
              — <Translated keyEn="(continued)" />
            </span>
          )}
        </FlexRow>
      </h2>
      {!isContinued && (
        <Row gutter={[10, 10]} style={{ width: "100%" }}>
          <Col xs={14}>
            <h3>
              <Translated keyEn="Site references" />
            </h3>
            <FlexCol style={{ gap: 0 }}>
              <FlexRow style={{ alignItems: "center" }}>
                <span>
                  <Translated keyEn="Client" />:
                </span>
                <PDFInput defaultValue={site.client_name} style={{ width: "fit-content" }} />
              </FlexRow>
              <FlexRow style={{ alignItems: "center" }}>
                <span>
                  <Translated keyEn="Mission" />:
                </span>
                <PDFInput defaultValue={site.mission_name} style={{ width: "fit-content" }} />
              </FlexRow>
              <FlexRow style={{ alignItems: "center" }}>
                <span>
                  <Translated keyEn="Inspection date" />:
                </span>
                <PDFInput
                  defaultValue={formatDayOrLongDash(site.execution_date, true)}
                  style={{ width: "fit-content" }}
                />
              </FlexRow>
              <FlexRow style={{ alignItems: "center" }}>
                <span>
                  <Translated keyEn="Inspection model" />:
                </span>
                <PDFInput
                  defaultValue={
                    template
                      ? `${template.Name} · v${template.Definition?.version ?? "?"}`
                      : "Version historique inconnue"
                  }
                  style={{ width: "fit-content" }}
                />
              </FlexRow>
            </FlexCol>
          </Col>
          <Col xs={10}>
            <FirstPicture pictures={site.pictures} />
          </Col>
        </Row>
      )}
      <EN1176QuestionnaireTable_Print
        controlPoints={controlPoints}
        answers={answers}
        referenceCodes={referenceCodes}
      />
    </FlexCol>
  )
}

const EquipmentPageContent: FC<{
  user: ICloudUser | null
  site: Site
  equipment: Equipment
  template: ICloudRegulatoryControlTemplate | null
  controlPoints: ICloudRegulatoryControlPoint[]
  answers: IEN1176AnswerMap
  referenceCodes: Record<string, string>
  equipmentPageMode: PDFEquipmentPageMode
  multiPageIndex: number
}> = ({
  equipment,
  template,
  controlPoints,
  answers,
  referenceCodes,
  equipmentPageMode,
  multiPageIndex,
}) => {
  if (equipmentPageMode === PDFEquipmentPageMode.MultiPage && multiPageIndex > 0) {
    return (
      <FlexCol
        style={{
          marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
          justifyContent: "start",
          height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
        }}
      >
        <h2 style={{ width: "100%" }}>
          <FlexRow style={{ alignItems: "center", width: "100%" }}>
            <span>
              <Translated keyEn="Equipment" />:
            </span>
            <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
          </FlexRow>
        </h2>
        <h3>
          <Translated keyEn="Control points" /> <Translated keyEn="(continued)" />
        </h3>
        <EN1176QuestionnaireTable_Print
          controlPoints={controlPoints}
          answers={answers}
          referenceCodes={referenceCodes}
        />
      </FlexCol>
    )
  }

  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      <h2 style={{ width: "100%" }}>
        <FlexRow style={{ alignItems: "center", width: "100%" }}>
          <span>
            <Translated keyEn="Equipment" />:
          </span>
          <PDFInput defaultValue={equipment.equipment_name} style={{ width: "fit-content" }} />
        </FlexRow>
      </h2>
      <Row gutter={[10, 10]} style={{ width: "100%" }}>
        <Col xs={14}>
          <h3>
            <Translated keyEn="Equipment references" />
          </h3>
          <FlexCol style={{ gap: 0 }}>
            <FlexRow style={{ alignItems: "center" }}>
              <span>
                <Translated keyEn="Manufacturer" />:
              </span>
              <PDFInput
                defaultValue={equipment.equipment_manufacturer}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            <FlexRow style={{ alignItems: "center" }}>
              <span>
                <Translated keyEn="Reference" />:
              </span>
              <PDFInput
                defaultValue={equipment.equipment_reference}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            <FlexRow style={{ alignItems: "center" }}>
              <span>
                <Translated keyEn="Installation date" />:
              </span>
              <PDFInput
                defaultValue={formatDayOrLongDash(equipment.equipment_installation_date, true)}
                style={{ width: "fit-content" }}
              />
            </FlexRow>
            <FlexRow style={{ alignItems: "center" }}>
              <span>
                <Translated keyEn="Inspection model" />:
              </span>
              <PDFInput
                defaultValue={
                  template
                    ? `${template.Name} · v${template.Definition?.version ?? "?"}`
                    : "Version historique inconnue"
                }
                style={{ width: "fit-content" }}
              />
            </FlexRow>
          </FlexCol>
        </Col>
        <Col xs={10}>
          <FirstPicture pictures={equipment.pictures} />
        </Col>
      </Row>
      <h3>
        <Translated keyEn="Control points" />
      </h3>
      <EN1176QuestionnaireTable_Print
        controlPoints={controlPoints}
        answers={answers}
        referenceCodes={referenceCodes}
      />
      {equipmentPageMode === PDFEquipmentPageMode.MultiPage && (
        <Typography.Text italic>
          <Translated keyEn="Table continued on next page..." />
        </Typography.Text>
      )}
    </FlexCol>
  )
}

const AnnexPageContent: FC<{
  annexes: Annex[]
}> = ({ annexes }) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      {annexes.map((annex, i) => {
        return (
          <div key={pbUUIDToUuid(annex.uuid)}>
            <h2>
              <Translated keyEn="Annex" /> {annex.annex_idx}
            </h2>
            <Row gutter={[10, 10]} justify="space-between">
              <Col xs={14}>
                <div style={PDF_INPUT_TEXT_AREA_STYLE}>
                  <PDFInputTextArea defaultValue={annex.annex_content} autoSize={{ minRows: 3 }} />
                </div>
              </Col>
              <Col xs={10}>
                <FirstPicture pictures={annex.pictures} />
              </Col>
            </Row>
            {i !== annexes.length - 1 && <Divider />}
          </div>
        )
      })}
      <PDFEmptySpacePlaceholder />
    </FlexCol>
  )
}

const ControlPointPhotoAnnexPageContent: FC<{
  entries: IEN1176ControlPointPhotoAnnex[]
}> = ({ entries }) => {
  return (
    <FlexCol
      style={{
        marginTop: PDF_PAGE_CONTENT_MARGIN_TOP,
        justifyContent: "start",
        height: `calc(100% - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
      }}
    >
      <h2>
        <Translated keyEn="Control point photo annexes" />
      </h2>
      <Row gutter={[16, 16]}>
        {entries.map((entry) => (
          <Col key={entry.key} xs={12}>
            <FlexCol style={{ gap: 6, alignItems: "center" }}>
              <Typography.Text strong style={{ textAlign: "center" }}>
                {entry.context} - {controlPointIDRender(entry.controlPoint.ID)}
              </Typography.Text>
              <FlexRow style={{ flexWrap: "wrap", justifyContent: "center", gap: 6 }}>
                {entry.pictures.map((pictureUUID) => (
                  <img
                    key={pbUUIDToUuid(pictureUUID)}
                    src={`/api/uploads/${pbUUIDToUuid(pictureUUID)}`}
                    alt={controlPointIDRender(entry.controlPoint.ID)}
                    style={{ maxWidth: "100%", maxHeight: 250, objectFit: "contain" }}
                  />
                ))}
              </FlexRow>
              {entry.comment && (
                <div style={{ width: "100%", whiteSpace: "pre-wrap" }}>{entry.comment}</div>
              )}
            </FlexCol>
          </Col>
        ))}
      </Row>
      <PDFEmptySpacePlaceholder />
    </FlexCol>
  )
}

export const EN1176ReportMasterPage: FC = () => {
  const { currentEnterprise } = useSyncronizationContext()

  const siteUUID = useMemo((): UUID | null => {
    let siteUUID = parsePathForEntityUUID(location.pathname)
    if (siteUUID === null) {
      return null
    }
    return uuidToPbUUID(siteUUID)
  }, [location])

  const [totalPages, setTotalPages] = useState(3)
  const [reportMode, setReportMode] = useState<EN1176ReportMode>("light")
  const [site, getSite] = useCloudUsercommSiteWS()
  const [user, setUser] = useState<ICloudUser | null>(null)
  const [enterpriseDevices, setEnterpriseDevices] = useState<ICloudDevice[]>([])
  const [selectedInspectionDeviceUUID, setSelectedInspectionDeviceUUID] = useState<string | null>(
    null,
  )
  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[] | null>(null)
  const [siteAnnexes, getSiteAnnexes] = useCloudUsercommSiteAnnexesWS()
  const [siteEquipmentsMap, , , getSiteChildrenRecursive, childrenError] = useRegulatoryChildren()

  const answerResource = useRegulatoryReportAnswers(siteUUID ? pbUUIDToUuid(siteUUID) : "")
  const { siteAnswers, equipmentAnswersMap } = answerResource

  // Site
  useEffect(() => {
    if (siteUUID === null) {
      return
    }
    getSite(siteUUID)
  }, [siteUUID])

  const siteEquipments = useMemo(() => {
    if (siteUUID === null) {
      return []
    }
    if (siteEquipmentsMap === null) {
      return []
    }
    const equipments = siteEquipmentsMap[pbUUIDToUuid(siteUUID)] ?? []
    console.log(
      `EN1176ReportPage: siteEquipmentsMap`,
      siteEquipmentsMap,
      `siteUUID`,
      siteUUID,
      `equipments`,
      equipments,
    )
    return equipments
      .filter((e) => e.deleted_at === 0)
      .sort((a, b) => a.equipment_name.localeCompare(b.equipment_name))
  }, [siteEquipmentsMap, siteUUID])

  const catalog = useAssignedCatalogs([
    {
      type: "site",
      uuid: site ? regulatoryUuid(site.uuid) : "",
      revision: site ? regulatoryUuid(site.regulatory_template_revision_uuid) : "",
    },
    ...siteEquipments.map((e) => ({
      type: "equipment" as const,
      uuid: regulatoryUuid(e.uuid),
      revision: regulatoryUuid(e.regulatory_template_revision_uuid),
    })),
  ])
  const regulatoryControlTemplates = catalog.templates

  useEffect(() => {
    if (siteUUID === null) {
      return
    }
    getSiteChildrenRecursive(siteUUID)
    getSiteAnnexes(siteUUID)
  }, [siteUUID, getSiteChildrenRecursive, getSiteAnnexes])

  useEffect(() => {
    if (site === null || !site.user_uuid) {
      return
    }
    callCloudApiV2<ICloudUser | null>(`/user?user_uuid=${pbUUIDToUuid(site.user_uuid)}`).then(
      ({ resp, entity: _user }) => {
        if (!resp.ok || _user === null) {
          console.error(`EN1176ReportPage: error fetching user`, resp)
        }
        setUser(_user)
      },
    )
  }, [site])

  useEffect(() => {
    if (user === null || user.Enterprise === null) {
      return
    }
    callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${user.Enterprise.UUID}`,
    ).then(({ resp, entity: _enterpriseCustomers }) => {
      if (!resp.ok || _enterpriseCustomers === null) {
        console.error(`EN1176ReportPage: error fetching enterprise customers`, resp)
      }
      setEnterpriseCustomers(_enterpriseCustomers)
    })
  }, [user])

  useEffect(() => {
    if (currentEnterprise === null) {
      return
    }
    callCloudApiV2<ICloudDevice[]>("/devices").then(({ entity }) => {
      setEnterpriseDevices(entity ?? [])
    })
  }, [currentEnterprise])

  const siteTemplateUUID = site ? regulatoryUuid(site.regulatory_template_uuid) : ""
  const siteTemplate = catalog.data?.[site ? regulatoryUuid(site.uuid) : ""]?.template ?? null
  const missionID = site ? regulatoryUuid(site.uuid) : ""
  const siteControlPoints = useMemo(
    () =>
      catalog.data?.[missionID]?.resolved
        ? catalog.pointsByTarget[missionID] ?? []
        : legacyControls(siteAnswers),
    [catalog.data, catalog.pointsByTarget, missionID, siteAnswers],
  )
  const equipmentControlPointsMap = useMemo(
    () =>
      Object.fromEntries(
        siteEquipments.map((e) => [
          pbUUIDToUuid(e.uuid),
          (() => {
            const assigned = catalog.data?.[regulatoryUuid(e.uuid)]
            const answers = equipmentAnswersMap[regulatoryUuid(e.uuid)] ?? {}
            if (!assigned?.resolved) return legacyControls(answers)
            const points = catalog.pointsByTarget[regulatoryUuid(e.uuid)] ?? []
            const unresolved = new Set(
              assigned.contextual?.configuration.contexts
                .filter((c) => c.assignments.some((a) => !a.templateRevisionUuid))
                .map((c) => c.uuid) ?? [],
            )
            return [
              ...points,
              ...legacyControls(answers)
                .filter((p) => p.AnswerContextUUID && unresolved.has(p.AnswerContextUUID))
                .map((p) => ({
                  ...p,
                  ContextLabel: assigned.contextual?.configuration.contexts.find(
                    (c) => c.uuid === p.AnswerContextUUID,
                  )?.label,
                })),
            ]
          })(),
        ]),
      ),
    [siteEquipments, catalog.pointsByTarget, catalog.data, equipmentAnswersMap],
  )

  const globalResultSummary = useMemo(() => {
    if (Object.values(catalog.data ?? {}).some((c) => !c.questionnaire.versioned))
      return "Version historique inconnue : les résultats enregistrés sont reproduits sans conclusion de conformité sur une grille reconstituée."
    return formatEN1176GlobalResultSummary(
      siteControlPoints,
      siteAnswers,
      siteEquipments,
      equipmentControlPointsMap,
      equipmentAnswersMap,
    )
  }, [
    siteControlPoints,
    siteAnswers,
    siteEquipments,
    equipmentControlPointsMap,
    equipmentAnswersMap,
    catalog.data,
  ])

  const identificationEquipmentChunks = useMemo(() => {
    const chunks: Equipment[][] = []
    for (
      let index = 0;
      index < siteEquipments.length;
      index += PDF_EN1176_MAX_IDENTIFICATION_EQUIPMENTS_PER_PAGE
    ) {
      chunks.push(
        siteEquipments.slice(index, index + PDF_EN1176_MAX_IDENTIFICATION_EQUIPMENTS_PER_PAGE),
      )
    }
    return chunks.length === 0 ? [[]] : chunks
  }, [siteEquipments])

  const reportReferences = useMemo(() => {
    const references = new Map<string, NonNullable<ICloudRegulatoryControlPoint["Reference"]>>()
    for (const point of [
      ...siteControlPoints,
      ...Object.values(equipmentControlPointsMap).flat(),
    ]) {
      if (point.Reference !== null) references.set(point.Reference.UUID, point.Reference)
    }
    return Array.from(references.values())
      .sort((left, right) => left.Name.localeCompare(right.Name))
      .map((reference, index) => ({
        ...reference,
        Code: `REF-${String(index + 1).padStart(2, "0")}`,
      }))
  }, [siteControlPoints, equipmentControlPointsMap])

  const referenceCodes = useMemo(
    () => Object.fromEntries(reportReferences.map((reference) => [reference.UUID, reference.Code])),
    [reportReferences],
  )

  const inspectionDevices = useMemo(
    () =>
      enterpriseDevices.filter(
        (device) =>
          device.DeviceType?.Name === "ValisGab1" || device.DeviceType?.Name === "ValiseGab2",
      ),
    [enterpriseDevices],
  )

  const controlPointPhotoAnnexes = useMemo(() => {
    const entries: IEN1176ControlPointPhotoAnnex[] = []
    const addEntries = (
      context: string,
      controlPoints: ICloudRegulatoryControlPoint[],
      answers: IEN1176AnswerMap,
    ) => {
      for (const controlPoint of controlPoints) {
        const answer = answers[controlAnswerKey(controlPoint)]
        if (!answer?.pictures?.length) continue
        answer.pictures.forEach((picture, index) =>
          entries.push({
            key: `${context}-${controlAnswerKey(controlPoint)}-${regulatoryUuid(picture)}`,
            context: `${context}${controlPoint.ContextLabel ? ` — ${controlPoint.ContextLabel}` : ""} · Photo ${index + 1}/${answer.pictures.length}`,
            controlPoint,
            pictures: [picture],
            comment: "Constat détaillé : voir la fiche de ce contrôle.",
          }),
        )
      }
    }

    addEntries("Site", siteControlPoints, siteAnswers)
    for (const equipment of siteEquipments) {
      const equipmentUUID = pbUUIDToUuid(equipment.uuid)
      addEntries(
        equipment.equipment_name || "Équipement",
        equipmentControlPointsMap[equipmentUUID] || [],
        equipmentAnswersMap[equipmentUUID] || {},
      )
    }
    return entries
  }, [
    siteControlPoints,
    siteAnswers,
    siteEquipments,
    equipmentControlPointsMap,
    equipmentAnswersMap,
  ])

  useEffect(() => {
    const firstInspectionDevice = inspectionDevices[0]
    if (firstInspectionDevice === undefined || selectedInspectionDeviceUUID !== null) {
      return
    }
    setSelectedInspectionDeviceUUID(firstInspectionDevice.UUID)
  }, [inspectionDevices, selectedInspectionDeviceUUID])

  const provenanceRows = useMemo(
    () =>
      Object.entries(catalog.data ?? {}).flatMap(([target, c]) => {
        const equipmentLabel =
          target === missionID
            ? site?.site_name
            : siteEquipments.find((e) => regulatoryUuid(e.uuid) === target)?.equipment_name
        if (c.contextual) {
          return c.contextual.configuration.contexts.flatMap((context) =>
            context.assignments.flatMap((assignment) => {
              const resolved = c.contextual!.resolvedAssignments[assignment.uuid]
              if (!resolved) return []
              return resolved.templates.map((module) => ({
                ...module,
                context: `${equipmentLabel} — ${context.label}`,
                missingParts: resolved.missingParts ?? [],
                classification: resolved.presentation,
                gaps: resolved.coverageGaps,
                reserves: resolved.interpretationReserves,
                sharing: assignment.bindings
                  .filter((b) => b.moduleRevisionUuid === module.revisionUuid)
                  .map(
                    (b) =>
                      `Constats partagés avec ${c.contextual!.configuration.contexts.find((x) => x.uuid === b.destinationContextUuid)?.label ?? b.destinationContextUuid}`,
                  )
                  .join(" ; "),
              }))
            }),
          )
        }
        return (c.resolved?.templates ?? []).map((module) => ({
          ...module,
          context: `${equipmentLabel} — ${c.template?.Name ?? "Version historique inconnue"}`,
          missingParts: c.resolved?.missingParts ?? [],
          classification: c.resolved?.presentation,
          gaps: c.resolved?.coverageGaps ?? 0,
          reserves: c.resolved?.interpretationReserves ?? 0,
          sharing: "",
        }))
      }),
    [catalog.data, missionID, site?.site_name, siteEquipments],
  )
  const provenanceChunks = useMemo(() => {
    const chunks: (typeof provenanceRows)[] = []
    for (let i = 0; i < provenanceRows.length; i += 3) chunks.push(provenanceRows.slice(i, i + 3))
    return chunks
  }, [provenanceRows])
  const legacyTargets = Object.values(catalog.data ?? {}).filter((c) => !c.questionnaire.versioned)
  const findings = [
    ...siteControlPoints.map((p) => ({
      ...p,
      ModuleName: `Mission — ${site?.site_name ?? ""}`,
      printAnswer: siteAnswers[controlAnswerKey(p)],
      Definition: undefined,
    })),
    ...siteEquipments.flatMap((e) =>
      (equipmentControlPointsMap[regulatoryUuid(e.uuid)] ?? []).map((p) => ({
        ...p,
        ModuleName: `Équipement — ${e.equipment_name}`,
        printAnswer: equipmentAnswersMap[regulatoryUuid(e.uuid)]?.[controlAnswerKey(p)],
        Definition: undefined,
      })),
    ),
  ].filter((p) =>
    [RegulatoryControlAnswerStatus.NON_CONFORM, RegulatoryControlAnswerStatus.OBSERVATION].includes(
      p.printAnswer?.status ?? RegulatoryControlAnswerStatus.UNKNOWN_STATUS,
    ),
  )
  const controlLayout = useMeasuredControlPages([
    { key: "findings", points: findings, answers: {}, firstHeight: 650 },
    {
      key: "site",
      points:
        reportMode === "light"
          ? getLightControlPoints(siteControlPoints, siteAnswers)
          : siteControlPoints,
      answers: siteAnswers,
      firstHeight: 380,
    },
    ...siteEquipments.map((e) => {
      const key = regulatoryUuid(e.uuid)
      const answers = equipmentAnswersMap[key] ?? {}
      const points = equipmentControlPointsMap[key] ?? []
      return {
        key,
        points: reportMode === "light" ? getLightControlPoints(points, answers) : points,
        answers,
        firstHeight: 200,
      }
    }),
  ])
  const findingsChunks = controlLayout.pages.findings ?? []
  const primaryPagesCount =
    identificationEquipmentChunks.length + 1 + provenanceChunks.length + findingsChunks.length

  useEffect(() => {
    let nbPages = 1 + primaryPagesCount // cover + primary report sections

    nbPages += controlLayout.pages.site?.length ?? 0
    for (const equipment of siteEquipments)
      nbPages += Math.max(1, controlLayout.pages[regulatoryUuid(equipment.uuid)]?.length ?? 0)

    const validAnnexes = (siteAnnexes || []).filter((a) => a.deleted_at === 0)
    nbPages += Math.ceil(controlPointPhotoAnnexes.length / 2)
    nbPages += Math.ceil(validAnnexes.length / 2)

    setTotalPages(nbPages)
  }, [
    controlLayout.pages,
    siteControlPoints,
    siteAnswers,
    siteEquipments,
    equipmentControlPointsMap,
    equipmentAnswersMap,
    siteAnnexes,
    primaryPagesCount,
    reportMode,
    controlPointPhotoAnnexes.length,
  ])

  const memoPrimaryPages = useMemo(() => {
    if (site === null) return []
    const pages: JSX.Element[] = identificationEquipmentChunks.map((equipments, index) => (
      <PDFPage
        key={`identification-${index}`}
        site={site}
        user={user}
        pageNumber={index === 0 ? 2 : index + 3}
        totalPages={totalPages}
      >
        <SecondPageContent
          site={site}
          equipments={equipments}
          templates={regulatoryControlTemplates}
          equipmentControlPointsMap={equipmentControlPointsMap}
          equipmentAnswersMap={equipmentAnswersMap}
          isIdentificationContinued={index > 0}
        />
      </PDFPage>
    ))
    pages.splice(
      1,
      0,
      <PDFPage
        key="references-inspection-equipment-geolocation"
        site={site}
        user={user}
        pageNumber={3}
        totalPages={totalPages}
      >
        <ThirdPageContent
          site={site}
          references={reportReferences}
          devices={inspectionDevices}
          selectedDeviceUUID={selectedInspectionDeviceUUID}
          setSelectedDeviceUUID={setSelectedInspectionDeviceUUID}
        />
      </PDFPage>,
    )
    provenanceChunks.forEach((rows, index) =>
      pages.push(
        <PDFPage
          key={`provenance-${index}`}
          site={site}
          user={user}
          pageNumber={identificationEquipmentChunks.length + 3 + index}
          totalPages={totalPages}
        >
          <Typography.Title level={3}>Modèles, révisions et limites de couverture</Typography.Title>
          <p>
            Révisions exactes affectées à chaque mission et équipement, incluant leurs modules. Les
            réponses historiques sans révision connue ne sont pas requalifiées.
          </p>
          {rows.map((row, i) => (
            <div
              key={`${row.revisionUuid}/${i}`}
              style={{ marginBottom: 16, overflowWrap: "anywhere" }}
            >
              <b>
                {row.context} — {row.name} · v{row.sourceVersion}
              </b>
              <br />
              Modèle : {row.templateUuid}
              <br />
              Révision : {row.revisionUuid}
              <br />
              {row.validated ? "Relu" : "Non relu"} · {row.coverage || "Couverture non renseignée"}
              <br />
              {row.gaps} compléments absents · {row.reserves} réserves.{" "}
              {row.missingParts.join(" ; ")}
              {row.sharing && <p>{row.sharing}</p>}
              {row.classification && (
                <p>
                  Classement de présentation :{" "}
                  {row.classification.uuid
                    ? `profil ${row.classification.uuid} · v${row.classification.version}`
                    : "proposition"}{" "}
                  · {row.classification.reviewed ? "revu" : "non revu"}. Distinct de la revue
                  réglementaire.
                </p>
              )}
            </div>
          ))}
        </PDFPage>,
      ),
    )
    findingsChunks.forEach((chunk, index) =>
      pages.push(
        <PDFPage
          key={`findings/${index}`}
          site={site}
          user={user}
          pageNumber={identificationEquipmentChunks.length + 3 + provenanceChunks.length + index}
          totalPages={totalPages}
        >
          <h2>Constats et actions demandées{index ? " — suite" : ""}</h2>
          <p>
            Non-conformités et observations relevées lors de cette inspection. La gravité est un
            classement interne.
          </p>
          <EN1176QuestionnaireTable_Print
            controlPoints={chunk}
            answers={{}}
            referenceCodes={referenceCodes}
          />
        </PDFPage>,
      ),
    )
    return pages
  }, [
    provenanceChunks,
    identificationEquipmentChunks,
    equipmentControlPointsMap,
    equipmentAnswersMap,
    inspectionDevices,
    primaryPagesCount,
    regulatoryControlTemplates,
    reportReferences,
    site,
    selectedInspectionDeviceUUID,
    totalPages,
    user,
    controlLayout.pages,
  ])

  const memoSiteQuestionnairePages = useMemo(() => {
    if (site === null || siteControlPoints.length === 0) {
      return []
    }
    const pages: JSX.Element[] = []
    const chunks = controlLayout.pages.site ?? []
    chunks.forEach((chunk, idx) => {
      const pageNumber = primaryPagesCount + 2 + idx
      pages.push(
        <PDFPage
          key={`site-cp-${idx}`}
          site={site}
          user={user}
          pageNumber={pageNumber}
          totalPages={totalPages}
        >
          <SiteQuestionnairePageContent
            site={site}
            template={siteTemplate}
            controlPoints={chunk}
            answers={siteAnswers}
            referenceCodes={referenceCodes}
            isContinued={idx > 0}
          />
        </PDFPage>,
      )
    })

    return pages
  }, [
    siteControlPoints,
    siteAnswers,
    site,
    user,
    totalPages,
    primaryPagesCount,
    referenceCodes,
    reportMode,
    siteTemplate,
    controlLayout.pages,
    catalog.data,
  ])

  const memoEquipmentPages = useMemo(() => {
    if (site === null || siteEquipments.length === 0) {
      return []
    }
    const pages: JSX.Element[] = []
    let pageNumber = primaryPagesCount + 2 + memoSiteQuestionnairePages.length

    for (const equipment of siteEquipments) {
      const equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
      const allControlPoints = equipmentControlPointsMap[equipmentUUIDStr] || []
      const answers = equipmentAnswersMap[equipmentUUIDStr] || {}
      const points =
        reportMode === "light" ? getLightControlPoints(allControlPoints, answers) : allControlPoints
      const template =
        regulatoryControlTemplates.find(
          (t) => t.UUID === regulatoryUuid(equipment.regulatory_template_uuid),
        ) || null

      const chunks = controlLayout.pages[equipmentUUIDStr]?.length
        ? controlLayout.pages[equipmentUUIDStr]!
        : [[]]
      chunks.forEach((chunk, index) => {
        pages.push(
          <PDFPage
            key={`${equipmentUUIDStr}/${index}`}
            site={site}
            user={user}
            pageNumber={pageNumber++}
            totalPages={totalPages}
          >
            <EquipmentPageContent
              user={user}
              site={site}
              equipment={equipment}
              template={catalog.data?.[equipmentUUIDStr]?.template ?? null}
              controlPoints={chunk}
              answers={answers}
              referenceCodes={referenceCodes}
              equipmentPageMode={
                chunks.length > 1 ? PDFEquipmentPageMode.MultiPage : PDFEquipmentPageMode.SinglePage
              }
              multiPageIndex={index}
            />
          </PDFPage>,
        )
      })
    }

    return pages
  }, [
    siteEquipments,
    equipmentControlPointsMap,
    equipmentAnswersMap,
    site,
    user,
    totalPages,
    memoSiteQuestionnairePages.length,
    primaryPagesCount,
    referenceCodes,
    reportMode,
    controlLayout.pages,
    catalog.data,
  ])

  const memoAnnexPages = useMemo(() => {
    if (siteAnnexes === null) {
      return []
    }
    const pages: JSX.Element[] = []
    const validAnnexes = siteAnnexes
      .filter((a) => a.deleted_at === 0)
      .sort((a, b) => a.annex_idx - b.annex_idx)

    let pageNumber =
      primaryPagesCount + 2 + memoSiteQuestionnairePages.length + memoEquipmentPages.length

    for (let i = 0; i < controlPointPhotoAnnexes.length; i += 2) {
      pages.push(
        <PDFPage
          key={`control-point-photo-annex-${i}`}
          site={site}
          user={user}
          pageNumber={pageNumber}
          totalPages={totalPages}
        >
          <ControlPointPhotoAnnexPageContent entries={controlPointPhotoAnnexes.slice(i, i + 2)} />
        </PDFPage>,
      )
      pageNumber += 1
    }

    for (let i = 0; i < validAnnexes.length; i += 2) {
      const _annexes = validAnnexes.slice(i, i + 2)
      pages.push(
        <PDFPage
          key={`annex-${i}`}
          site={site}
          user={user}
          pageNumber={pageNumber}
          totalPages={totalPages}
        >
          <AnnexPageContent annexes={_annexes} />
        </PDFPage>,
      )
      pageNumber += 1
    }

    return pages
  }, [
    siteAnnexes,
    site,
    user,
    totalPages,
    memoSiteQuestionnairePages.length,
    memoEquipmentPages.length,
    primaryPagesCount,
    controlPointPhotoAnnexes,
  ])

  const missingAssignment =
    site &&
    (!siteTemplateUUID || siteEquipments.some((e) => !regulatoryUuid(e.regulatory_template_uuid)))
  if (
    childrenError ||
    catalog.error ||
    answerResource.error ||
    !answerResource.connected ||
    missingAssignment
  )
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Rapport indisponible"
          description={
            childrenError ||
            catalog.error?.message ||
            answerResource.error ||
            (missingAssignment
              ? "Affectez un modèle au site et à chaque équipement avant de générer le rapport."
              : "La connexion cloud est nécessaire pour charger les réponses.")
          }
        />
        <Button
          onClick={() => {
            catalog.reload()
            answerResource.reload()
          }}
        >
          Réessayer
        </Button>
        <Link to={`/regulatory/sites/${siteUUID ? pbUUIDToUuid(siteUUID) : ""}`}>
          Retour au site
        </Link>
      </div>
    )

  if (
    user === null ||
    site === null ||
    siteAnnexes === null ||
    siteEquipmentsMap === null ||
    catalog.loading ||
    answerResource.loading
  ) {
    return (
      <FlexCol
        style={{
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </FlexCol>
    )
  }

  return (
    <ReportConfigProvider>
      <div className="pdf-invisible" style={{ padding: 16 }}>
        <Alert
          type="info"
          message="Définitions résolues pour ce rapport"
          description="Le rapport utilise les révisions affectées à la mission et à chaque équipement. Les versions historiques inconnues restent signalées."
        />
      </div>
      {controlLayout.measuring}
      <div id="pdf" className="A4">
        <canvas id="text-measurement-canvas" style={{ display: "none" }} />
        <PDFPage site={site} user={user} pageNumber={1} totalPages={totalPages}>
          {!!legacyTargets.length && (
            <p>
              Version historique inconnue pour {legacyTargets.length} affectation(s). Les réponses
              enregistrées sont présentées sans reconstituer leurs anciennes définitions.
            </p>
          )}
          <Typography.Text style={{ fontSize: 10 }}>
            Définitions des révisions affectées à cette inspection. Le catalogue décrit les
            contrôles, sans déterminer la conformité réglementaire globale.
          </Typography.Text>
          <FirstPageContent
            user={user}
            site={site}
            globalResultSummary={globalResultSummary}
            enterpriseCustomers={enterpriseCustomers}
          />
        </PDFPage>
        {memoPrimaryPages}
        {memoSiteQuestionnairePages}
        {memoEquipmentPages}
        {memoAnnexPages}
      </div>
      <div className="pdf-invisible">
        <FloatButton
          icon={<FilterOutlined />}
          description={reportMode === "full" ? "Complet" : "Allégé"}
          type={reportMode === "light" ? "primary" : "default"}
          style={{ width: 60, height: 60, right: 250 }}
          onClick={() => setReportMode((mode) => (mode === "full" ? "light" : "full"))}
        />
        <Popover content={<TranslationLanguageSelectWidget />}>
          <FloatButton
            icon={<GlobalOutlined />}
            description={<Translated keyEn="Lang" />}
            type="default"
            style={{ width: 60, height: 60, right: 180 }}
          />
        </Popover>
        <Link to={`/sites/${pbUUIDToUuid(site.uuid)}`}>
          <FloatButton
            icon={<LeftCircleOutlined />}
            description={<Translated keyEn="Back" />}
            type="default"
            style={{ width: 60, height: 60, right: 110 }}
          />
        </Link>
        <FloatButton
          icon={<FilePdfOutlined />}
          description={<Translated keyEn="Print" />}
          aria-disabled={!controlLayout.ready}
          type="primary"
          style={{ width: 60, height: 60, right: 40 }}
          onClick={() => {
            if (controlLayout.ready) window.print()
          }}
        />
      </div>
    </ReportConfigProvider>
  )
}
